import { useEffect, useMemo, useRef } from 'react';

import type { GridApi } from '@mui/x-data-grid-premium';
import {
  gridDataRowIdsSelector,
  gridRowNodeSelector,
  gridRowSelectionIdsSelector,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import type { TotalDocumentCount } from 'src/components';

/**
 * Determines whether all loaded (non-skeleton) rows in the grid are currently selected.
 * Uses grid selectors (instead of imperative `getRowModels()`) so the value is memoized
 * and only recomputed when the underlying selection or row data actually changes.
 * Uses a Set for O(1) membership checks instead of Array.includes.
 */
export function useAreAllRowsSelected(
  apiRef: React.RefObject<GridApi>,
  totalDocumentCount: TotalDocumentCount
): { areAllLoadedRowsSelected: boolean; shouldRunForAllDocuments: boolean } {
  const dataRowIds = useGridSelector(apiRef, gridDataRowIdsSelector);
  const selectionIds = useGridSelector(apiRef, gridRowSelectionIdsSelector);

  const areAllLoadedRowsSelected = useMemo(() => {
    const nonSkeleton = dataRowIds
      .filter(id => gridRowNodeSelector(apiRef, id)?.type !== 'skeletonRow')
      .map(String);
    if (!nonSkeleton.length) return false;
    const selected = new Set(Array.from(selectionIds.keys()).map(String));
    return nonSkeleton.every(id => selected.has(id));
  }, [apiRef, dataRowIds, selectionIds]);

  const shouldRunForAllDocuments =
    areAllLoadedRowsSelected && totalDocumentCount !== 'no-select-all' && totalDocumentCount > 0;

  return { areAllLoadedRowsSelected, shouldRunForAllDocuments };
}

/**
 * Keeps "Select All" sticky as lazy-loaded rows arrive.
 *
 * In dataSource + lazy-loading grids (and any time `isRowSelectable` is set),
 * MUI X uses include-mode selection — the header checkbox materializes an
 * explicit ID set rather than the cheaper "all except these" exclude set.
 * Rows fetched after that click are NOT auto-added, so they arrive unchecked.
 * This hook subscribes to row/selection events, detects newly-loaded rows, and
 * appends them to the selection — as long as the user hadn't manually
 * unchecked anything since the last fetch.
 *
 * Uses imperative `subscribeEvent` rather than `useGridSelector` so it can be
 * called from the page level alongside the nullable ref returned by
 * `useGridApiRef()`.
 */
export function useStickySelectAllAcrossFetches(apiRef: React.RefObject<GridApi | null>): void {
  const prevDataRowIdsRef = useRef<Set<string>>(new Set());
  const wasAllSelectedRef = useRef(false);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    const sync = () => {
      if (apiRef.current !== api) return;
      const currentIds = getNonSkeletonRowIds(apiRef);
      const selectedSet = getSelectedRowIds(apiRef);
      const newIds = currentIds.filter(id => !prevDataRowIdsRef.current.has(id));

      // Update bookkeeping BEFORE any API call: `setRowSelectionModel` fires
      // `rowSelectionChange` synchronously and re-enters this handler.
      prevDataRowIdsRef.current = new Set(currentIds);

      if (newIds.length > 0 && wasAllSelectedRef.current) {
        // Replace, don't merge: a partial-load select-all can leak skeleton-row
        // ids into `selectedSet` that `keepNonExistentRowsSelected` won't evict.
        api.setRowSelectionModel({
          ids: new Set(currentIds),
          type: 'include',
        });
        return;
      }
      wasAllSelectedRef.current =
        currentIds.length > 0 && currentIds.every(id => selectedSet.has(id));
    };

    sync();
    const unsubs = [
      api.subscribeEvent('rowsSet', sync),
      api.subscribeEvent('rowSelectionChange', sync),
    ];
    return () => unsubs.forEach(u => u());
  }, [apiRef]);
}

function getNonSkeletonRowIds(apiRef: React.RefObject<GridApi | null>): string[] {
  return gridDataRowIdsSelector(apiRef)
    .filter(id => gridRowNodeSelector(apiRef, id)?.type !== 'skeletonRow')
    .map(String);
}

function getSelectedRowIds(apiRef: React.RefObject<GridApi | null>): Set<string> {
  return new Set(Array.from(gridRowSelectionIdsSelector(apiRef).keys()).map(String));
}
